import { devlog } from "./utils.ts";

export type HistoryAction =
  | {
      type: 'configure';
      paramsBefore: Record<string, any>;
      paramsAfter:  Record<string, any>;
      label?: string;
      tags?: string[];
      customDataBefore?: Record<string, any>;
      customDataAfter?:  Record<string, any>;
      clearsRedoOnUndo?: boolean;
    }
  | { type: 'custom-data'; before: Record<string, any>; after: Record<string, any>; label?: string; tags?: string[]; clearsRedoOnUndo?: boolean }
  | { type: 'input'; inputId: string; valueBefore: any; valueAfter: any; label?: string; tags?: string[]; clearsRedoOnUndo?: boolean };

let _stack: HistoryAction[] = [];
let _head = -1;
let _maxHistory = 50;
let _inProgress = false;
let _enabled = true;

let _historyBatchActive = false;
let _historyBatchBuffer: HistoryAction[] = [];
let _historyBatchLabel: string | undefined;
let _historyBatchTags: string[] | undefined;

export function setMaxHistory(v: number): void { _maxHistory = Math.max(1, v); }
export function setHistoryEnabled(v: boolean): void { _enabled = v; }
export function isHistoryEnabled(): boolean { return _enabled; }
export function canUndo(): boolean { return _head >= 0; }
export function canRedo(): boolean { return _head < _stack.length - 1; }
export function inProgress(): boolean { return _inProgress; }
export function isHistoryBatchActive(): boolean { return _historyBatchActive; }

/** Peek at the action that would be undone next. */
export function peekUndo(): HistoryAction | null {
  return _head >= 0 ? _stack[_head] : null;
}

/** Push a new action. Returns entries removed from the stack (for token cleanup). */
export function pushAction(action: HistoryAction, label?: string): HistoryAction[] {
  if (!_enabled) return [];
  if (label) action.label = label;

  // Buffer into active history batch instead of pushing to the stack.
  if (_historyBatchActive) {
    devlog(`[HISTORY] Buffering ${action.type} into history batch`);
    _historyBatchBuffer.push(action);
    return [];
  }

  devlog(`[HISTORY] pushAction type=${action.type} label=${label || 'none'} stackSize=${_stack.length} head=${_head}`);

  // Ignore if identical to the action already at head — prevents double entries
  // from redundant event triggers.
  if (_head >= 0 && _areActionsIdentical(_stack[_head], action)) {
    devlog(`[HISTORY] Ignoring identical ${action.type} action at head`);
    return [];
  }

  const truncated = _stack.splice(_head + 1);
  if (truncated.length) devlog(`[HISTORY] Truncated ${truncated.length} items from head`);
  _stack.push(action);
  const dropped: HistoryAction[] = [];
  while (_stack.length > _maxHistory) {
    const d = _stack.shift();
    if (d) dropped.push(d);
  }
  _head = _stack.length - 1;
  _dispatch();
  return [...truncated, ...dropped];
}


/** Begin an undo. Returns the action to revert, or null if unavailable. */
export function undoAction(): HistoryAction | null {
  if (!canUndo() || _inProgress) return null;
  _inProgress = true;
  return _stack[_head--];
}

/** Begin a redo. Returns the action to re-apply, or null if unavailable. */
export function redoAction(): HistoryAction | null {
  if (!canRedo() || _inProgress) return null;
  _inProgress = true;
  return _stack[++_head];
}

/** Call when the async undo/redo operation is complete. */
export function completeAction(action?: HistoryAction, direction?: 'undo' | 'redo'): void {
  _inProgress = false;
  _dispatch(action, direction);
}

/** Truncate the stack at the current head, removing all redo actions. Returns removed items. */
export function truncateRedo(): HistoryAction[] {
  const truncated = _stack.splice(_head + 1);
  _dispatch();
  return truncated;
}

/** Clear the stack. Returns all entries (for token cleanup). */
export function resetHistory(): HistoryAction[] {
  const all = [..._stack];
  _stack = [];
  _head = -1;
  _inProgress = false;
  _dispatch();
  return all;
}

export function getStack(): readonly HistoryAction[] { return _stack; }
export function getHead(): number { return _head; }

// ============================================================================
// History batches — collapse multiple pushAction calls into a single undo entry
// ============================================================================

/**
 * Open a history batch. All pushAction calls until finishHistoryBatch() are
 * buffered and collapsed into a single history entry on close.
 * Nested calls are ignored — the outermost caller owns the batch.
 *
 * @param label - Optional label for the merged entry.
 * @param tags  - Optional string tags included in lifecycle events and the merged action,
 *                useful for identifying batches in external listeners.
 *
 * Dispatches: `as-history-batch-start` with `{ label, tags }`.
 */
export function startHistoryBatch(label?: string, tags?: string[]): void {
  if (_historyBatchActive) {
    devlog('[HISTORY] startHistoryBatch: already active, ignoring nested call');
    return;
  }
  _historyBatchActive = true;
  _historyBatchBuffer = [];
  _historyBatchLabel = label;
  _historyBatchTags = tags;
  devlog(`[HISTORY] startHistoryBatch label=${label || 'none'} tags=${tags?.join(',') || 'none'}`);
  window.dispatchEvent(new CustomEvent('as-history-batch-start', { detail: { label, tags } }));
}

/**
 * Close the history batch and push a single merged action to the stack.
 * Returns the same dropped-entries array as pushAction() (for token cleanup).
 *
 * Dispatches: `as-history-batch-end` with `{ label, tags, action }`.
 */
export function finishHistoryBatch(): HistoryAction[] {
  if (!_historyBatchActive) return [];
  _historyBatchActive = false;

  const buffer = _historyBatchBuffer;
  const label  = _historyBatchLabel;
  const tags   = _historyBatchTags;
  _historyBatchBuffer = [];
  _historyBatchLabel  = undefined;
  _historyBatchTags   = undefined;

  devlog(`[HISTORY] finishHistoryBatch: ${buffer.length} buffered action(s)`);

  if (buffer.length === 0) {
    window.dispatchEvent(new CustomEvent('as-history-batch-end', { detail: { label, tags, action: null } }));
    return [];
  }

  if (buffer.length === 1) {
    const action = buffer[0];
    if (label) action.label = label;
    if (tags)  action.tags  = tags;
    const dropped = pushAction(action);
    window.dispatchEvent(new CustomEvent('as-history-batch-end', { detail: { label, tags, action } }));
    return dropped;
  }

  const merged = _mergeBuffer(buffer, label, tags);

  // Merged result may be an array in edge cases (mixed inputs that can't collapse).
  if (Array.isArray(merged)) {
    let allDropped: HistoryAction[] = [];
    for (const a of merged) allDropped = [...allDropped, ...pushAction(a)];
    window.dispatchEvent(new CustomEvent('as-history-batch-end', { detail: { label, tags, action: merged[merged.length - 1] ?? null } }));
    return allDropped;
  }

  const dropped = pushAction(merged);
  window.dispatchEvent(new CustomEvent('as-history-batch-end', { detail: { label, tags, action: merged } }));
  return dropped;
}

/**
 * Discard the active history batch without pushing anything.
 * Dispatches: `as-history-batch-cancel` with `{ label, tags }`.
 */
export function cancelHistoryBatch(): void {
  if (!_historyBatchActive) return;
  const label = _historyBatchLabel;
  const tags  = _historyBatchTags;
  _historyBatchActive = false;
  _historyBatchBuffer = [];
  _historyBatchLabel  = undefined;
  _historyBatchTags   = undefined;
  devlog(`[HISTORY] cancelHistoryBatch label=${label || 'none'}`);
  window.dispatchEvent(new CustomEvent('as-history-batch-cancel', { detail: { label, tags } }));
}

// ============================================================================
// Internal helpers
// ============================================================================

function _areActionsIdentical(last: HistoryAction, next: HistoryAction): boolean {
  if (last.type !== next.type) return false;
  if (last.type === 'input' && next.type === 'input')
    return last.inputId === next.inputId && last.valueAfter === next.valueAfter;
  if (last.type === 'custom-data' && next.type === 'custom-data')
    return JSON.stringify(last.after) === JSON.stringify(next.after);
  if (last.type === 'configure' && next.type === 'configure')
    return JSON.stringify(last.paramsAfter) === JSON.stringify(next.paramsAfter);
  return false;
}

/**
 * Merge a buffer of actions into the fewest possible entries.
 * Returns a single HistoryAction, or an array when inputs can't be collapsed.
 */
function _mergeBuffer(
  buffer: HistoryAction[],
  label: string | undefined,
  tags: string[] | undefined
): HistoryAction | HistoryAction[] {
  const clears = buffer.some(a => a.clearsRedoOnUndo);

  const configures   = buffer.filter((a): a is HistoryAction & { type: 'configure' }   => a.type === 'configure');
  const customDatas  = buffer.filter((a): a is HistoryAction & { type: 'custom-data' } => a.type === 'custom-data');
  const inputs       = buffer.filter((a): a is HistoryAction & { type: 'input' }       => a.type === 'input');

  // configure (+ optional custom-data) → single configure
  if (configures.length > 0) {
    const first = configures[0];
    const last  = configures[configures.length - 1];
    const firstCd = customDatas[0];
    const lastCd  = customDatas[customDatas.length - 1];
    return {
      type: 'configure',
      paramsBefore:     first.paramsBefore,
      paramsAfter:      last.paramsAfter,
      customDataBefore: firstCd?.before ?? first.customDataBefore,
      customDataAfter:  lastCd?.after   ?? last.customDataAfter,
      label,
      tags,
      clearsRedoOnUndo: clears || undefined,
    };
  }

  // only custom-data → single custom-data
  if (customDatas.length > 0 && inputs.length === 0) {
    return {
      type:  'custom-data',
      before: customDatas[0].before,
      after:  customDatas[customDatas.length - 1].after,
      label,
      tags,
      clearsRedoOnUndo: clears || undefined,
    };
  }

  // only inputs with the same inputId → single input
  const uniqueIds = new Set(inputs.map(a => a.inputId));
  if (inputs.length > 0 && configures.length === 0 && uniqueIds.size === 1) {
    return {
      type:        'input',
      inputId:     inputs[0].inputId,
      valueBefore: inputs[0].valueBefore,
      valueAfter:  inputs[inputs.length - 1].valueAfter,
      label,
      tags,
      clearsRedoOnUndo: clears || undefined,
    };
  }

  // Mixed or multi-inputId inputs: push individually (batch emits multiple entries)
  devlog('[HISTORY] finishHistoryBatch: mixed buffer, pushing individually');
  return buffer;
}

function _dispatch(action?: HistoryAction, direction?: 'undo' | 'redo'): void {
  window.dispatchEvent(new CustomEvent('as-history-changed', {
    detail: {
      canUndo: canUndo(), canRedo: canRedo(),
      head: _head, length: _stack.length,
      ...(action    && { lastAction: action }),
      ...(direction && { direction }),
    }
  }));
}
